import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { TableColumnsType, TablePaginationConfig } from "antd";
import type { Dayjs } from "dayjs";
import {
  ClearOutlined,
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import useGetOrders from "../../hooks/getOrders/useGetOrders";
import useReprocessOrders from "../../hooks/reprocessOrders/useReprocessOrders";
import ExportTableButton from "layouts/LayoutTableExport/LayoutTableExport";
import { TimeFormatter } from "helpers/TimeFormatter/TimeFormatter";
import { PriceFormatter } from "helpers/PriceFormatter/PriceFormatter";
import OrderReprocessProgressModal from "../../components/OrderReprocessProgress/OrderReprocessProgress";
import OrderStatusTag from "../../components/OrderStatusTag/OrderStatusTag";
import CopyableTruncatedCell from "../../components/CopyableTruncatedCell";
import TableSkeleton from "../../ui/table/TableSkeleton";
import { TABLE_PAGINATION_UX } from "../../ui/table/paginationDefaults";
import { getVtexAdminOrderUrl } from "../../config/gateway";
import type {
  OrderFilterType,
  OrderListFilters,
  OrderListItem,
} from "../../types/orders";
import {
  EMPTY_ORDER_FILTERS,
  ORDER_FILTER_TYPE_OPTIONS,
  ORDER_FILTER_TYPE_PLACEHOLDERS,
  ORDER_LIST_WINDOW_MONTHS,
  ORDER_REPROCESS_MAX_BATCH,
  ORDER_STATUS_FILTER_OPTIONS,
  canReprocessOrder,
  getOrderListDateWindow,
  getOrderStatusLabel,
  getReprocessIneligibilityReason,
  resolveOrderListTotalElements,
} from "../../types/orders";
import "./OrdersPage.css";

const EXPORT_TRUNCATED_MSG_KEY = "orders-export-truncated";

/**
 * §3.2: list-orders dates must stay inside [today − 2 months, today]
 * (America/Bogota on backend). Client uses local calendar as UX guard.
 */
const isOrderListDateOutsideWindow = (current: Dayjs | null): boolean => {
  if (!current) return false;
  const { min, max } = getOrderListDateWindow();
  return current.isAfter(max, "day") || current.isBefore(min, "day");
};

/**
 * Column order (ops scan path):
 * 1) Order identity → 2) Status → 3) Creation → 4) Customer →
 * 5) Amount → 6) Logistics → 7) Process time → 8) Retries → 9) Error
 */
const COLUMN_DEFS = [
  {
    title: "Order VTEX",
    tooltip: "Order VTEX",
    dataIndex: "orderVTEXId",
    key: "orderVTEXId",
  },
  {
    title: "Order SIESA",
    tooltip: "Order SIESA",
    dataIndex: "orderSIESAId",
    key: "orderSIESAId",
  },
  {
    title: "Estado",
    tooltip: "Estado",
    dataIndex: "status",
    key: "status",
  },
  {
    title: "Nombre",
    tooltip: "Nombre Cliente",
    dataIndex: "names",
    key: "names",
  },
  {
    title: "Doc. Cliente",
    tooltip: "Documento Cliente",
    dataIndex: "documentId",
    key: "documentId",
  },
  {
    title: "Total",
    tooltip: "Total",
    dataIndex: "total",
    key: "total",
  },
  {
    title: "Núm. Tracking",
    tooltip: "Número Tracking",
    dataIndex: "trackingNumber",
    key: "trackingNumber",
  },
  {
    title: "F. Procesado",
    tooltip: "Fecha de Procesado",
    dataIndex: "processedAt",
    key: "processedAt",
  },
  {
    title: "Reint. Aut.",
    tooltip: "Reintentos automáticos",
    dataIndex: "retryCount",
    key: "retryCount",
  },
  {
    title: "Reint. Man.",
    tooltip: "Reintentos manuales",
    dataIndex: "attemptCount",
    key: "attemptCount",
  },
  {
    title: "Det. Error",
    tooltip: "Detalle Error",
    dataIndex: "errorDetail",
    key: "errorDetail",
  },
] as const;

type OrderColumnKey = (typeof COLUMN_DEFS)[number]["key"];

const DEFAULT_COLUMNS: OrderColumnKey[] = [
  "orderVTEXId",
  "orderSIESAId",
  "status",
  "names",
  "documentId",
  "total",
  "trackingNumber",
  "processedAt",
  "retryCount",
  "attemptCount",
  "errorDetail",
];

/**
 * Short header label + full tooltip text.
 * ASSUMPTION: tooltip always shows the complete column name.
 */
const renderColumnTitle = (label: string, tooltip: string) => (
  <Tooltip title={tooltip} placement="top">
    <span className="orders-page__th-label">{label}</span>
  </Tooltip>
);

/**
 * Error cell: link + popover only when text is truncated (more content exists).
 * Fully visible text stays plain red (not clickable).
 *
 * ASSUMPTION: `-webkit-line-clamp` does not inflate scrollHeight, so we measure
 * unrestricted text height against 3 visible lines.
 */
const ERROR_DETAIL_VISIBLE_LINES = 3;

const OrderErrorDetailCell: React.FC<{ value: string }> = ({ value }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const update = () => {
      const width = el.clientWidth;
      if (width <= 0) {
        setIsTruncated(false);
        return;
      }

      const styles = window.getComputedStyle(el);
      const probe = document.createElement("div");
      probe.textContent = value;
      probe.style.position = "absolute";
      probe.style.visibility = "hidden";
      probe.style.pointerEvents = "none";
      probe.style.left = "-9999px";
      probe.style.top = "0";
      probe.style.width = `${width}px`;
      probe.style.font = styles.font;
      probe.style.fontSize = styles.fontSize;
      probe.style.fontWeight = styles.fontWeight;
      probe.style.fontFamily = styles.fontFamily;
      probe.style.lineHeight = styles.lineHeight;
      probe.style.letterSpacing = styles.letterSpacing;
      probe.style.wordBreak = "break-word";
      probe.style.whiteSpace = "normal";
      document.body.appendChild(probe);

      const parsedLineHeight = Number.parseFloat(styles.lineHeight);
      const lineHeight =
        Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
          ? parsedLineHeight
          : Number.parseFloat(styles.fontSize) * 1.35;
      const maxVisibleHeight = lineHeight * ERROR_DETAIL_VISIBLE_LINES;
      setIsTruncated(probe.scrollHeight > maxVisibleHeight + 1);
      document.body.removeChild(probe);
    };

    update();
    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Detalle de error copiado");
    } catch {
      message.error("No se pudo copiar");
    }
  };

  return (
    <Popover
      title="Detalle error"
      trigger={isTruncated ? ["hover", "click"] : []}
      placement="leftTop"
      mouseEnterDelay={0.25}
      mouseLeaveDelay={0.35}
      overlayClassName="copyable-truncated-cell__popover"
      content={
        <div className="copyable-truncated-cell__popover-body copyable-truncated-cell__popover-body--multiline">
          <div className="orders-page__error-popover copyable-truncated-cell__popover-value">
            {value}
          </div>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            aria-label="Copiar detalle de error"
            title="Copiar"
          />
        </div>
      }
    >
      <span
        ref={textRef}
        className={
          isTruncated
            ? "orders-page__error-preview"
            : "orders-page__error-preview orders-page__error-preview--plain"
        }
        role={isTruncated ? "button" : undefined}
        tabIndex={isTruncated ? 0 : undefined}
        title={
          isTruncated
            ? "Pasa el cursor o haz clic para ver y copiar el detalle"
            : undefined
        }
      >
        {value}
      </span>
    </Popover>
  );
};

const OrdersPage: React.FC = () => {
  const {
    orders,
    loading,
    error,
    totalElements,
    filteredCount,
    filteredCountTruncated,
    filteredCountLoading,
    fetchOrders,
    fetchOrdersForExport,
    fetchFilteredCount,
  } = useGetOrders();

  const refreshListRef = useRef<() => void>(() => undefined);

  const {
    reprocessLoading,
    progressOpen,
    progress,
    error: reprocessError,
    startReprocess,
    closeProgress,
  } = useReprocessOrders({
    onComplete: () => {
      refreshListRef.current();
    },
  });

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedColumns, setSelectedColumns] =
    useState<OrderColumnKey[]>(DEFAULT_COLUMNS);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<OrderListFilters>(EMPTY_ORDER_FILTERS);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const activeFilters = useMemo<OrderListFilters>(
    () => ({
      ...filters,
      search: appliedSearch,
      dateFrom: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null,
      dateTo: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null,
    }),
    [filters, appliedSearch, dateRange]
  );

  /**
   * Backend `totalElements` ignores status/search (fixed status subset), so
   * those filters need the exact background count. The unfiltered and
   * date-only views keep the backend total.
   */
  const hasNarrowingFilter =
    activeFilters.status !== "ALL" || Boolean(activeFilters.search.trim());

  const submitSearch = useCallback((term?: string) => {
    const nextTerm = (term ?? searchValue).trim();
    setAppliedSearch(nextTerm);
    setPagination((prev) =>
      prev.current === 1 ? prev : { ...prev, current: 1 }
    );
  }, [searchValue]);

  refreshListRef.current = () => {
    setSelectedRowKeys([]);
    void fetchOrders(
      pagination.current || 1,
      pagination.pageSize || 10,
      activeFilters
    );
    if (hasNarrowingFilter) {
      void fetchFilteredCount(activeFilters);
    }
  };

  useEffect(() => {
    if (
      activeFilters.dateFrom &&
      activeFilters.dateTo &&
      activeFilters.dateFrom > activeFilters.dateTo
    ) {
      message.error(
        "Rango de fechas inválido: la fecha inicio no puede ser mayor que la fin."
      );
      return;
    }

    const page = pagination.current || 1;
    const pageSize = pagination.pageSize || 10;
    void fetchOrders(page, pageSize, activeFilters);
    // ASSUMPTION: depend on page/size scalars only — including `pagination`
    // would refetch when `total` updates and can loop with the total sync effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page/size/filters only
  }, [
    fetchOrders,
    pagination.current,
    pagination.pageSize,
    activeFilters,
  ]);

  useEffect(() => {
    if (
      activeFilters.dateFrom &&
      activeFilters.dateTo &&
      activeFilters.dateFrom > activeFilters.dateTo
    ) {
      return;
    }
    if (!hasNarrowingFilter) return;
    void fetchFilteredCount(activeFilters);
    // Page/size changes do not affect the filtered universe — only recount
    // when the filters themselves change.
  }, [fetchFilteredCount, hasNarrowingFilter, activeFilters]);

  useEffect(() => {
    if (reprocessError) {
      message.error(reprocessError);
    }
  }, [reprocessError]);

  const orderData = useMemo(() => orders ?? [], [orders]);

  const currentPage = pagination.current || 1;
  const currentPageSize = pagination.pageSize || 10;

  const paginationTotal = useMemo(() => {
    // Exact background count for status/search filters once it resolves;
    // the unfiltered/date-only view uses the backend's total.
    if (hasNarrowingFilter && filteredCount !== null) {
      return filteredCount;
    }
    return resolveOrderListTotalElements(
      totalElements,
      orderData.length,
      currentPage,
      currentPageSize
    );
  }, [
    hasNarrowingFilter,
    filteredCount,
    totalElements,
    orderData.length,
    currentPage,
    currentPageSize,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: paginationTotal }));
  }, [paginationTotal]);

  const isBusy = loading || reprocessLoading;
  const showTableSkeleton = loading && orderData.length === 0;

  const columns: TableColumnsType<OrderListItem> = useMemo(() => {
    const defs = COLUMN_DEFS.filter((col) =>
      selectedColumns.includes(col.key)
    );

    const renderDateTime = (value: string | null) => {
      if (!value) return "—";
      const formatted = TimeFormatter(value);
      if (formatted === "Invalid date") return value;
      const [datePart, timePart] = formatted.split(" ");
      return (
        <span className="orders-page__datetime" title={formatted}>
          <span className="orders-page__datetime-date">{datePart}</span>
          {timePart ? (
            <span className="orders-page__datetime-time">{timePart}</span>
          ) : null}
        </span>
      );
    };

    return defs.map((col) => {
      const title = renderColumnTitle(col.title, col.tooltip);

      // ASSUMPTION: 14" laptop (~1366px) with 300px sidebar leaves ~1000px
      // for the table; widths below sum to ~980px incl. selection column.
      if (col.key === "status") {
        return {
          title,
          key: col.key,
          dataIndex: "status",
          width: 118,
          render: (value: string) => <OrderStatusTag status={value} />,
        };
      }
      if (col.key === "processedAt") {
        return {
          title,
          key: col.key,
          dataIndex: col.dataIndex,
          width: 84,
          render: (value: string | null) => renderDateTime(value),
        };
      }
      if (col.key === "total") {
        return {
          title,
          key: col.key,
          dataIndex: "total",
          width: 110,
          className: "orders-page__col--total",
          onHeaderCell: () => ({ className: "orders-page__col--total" }),
          onCell: () => ({ className: "orders-page__col--total" }),
          render: (value: number) => {
            const formatted = PriceFormatter(value);
            return (
              <CopyableTruncatedCell
                value={formatted}
                copySuccessMessage="Total copiado"
                onlyWhenTruncated={false}
              />
            );
          },
        };
      }
      if (col.key === "errorDetail") {
        return {
          title,
          key: col.key,
          dataIndex: "errorDetail",
          width: 140,
          render: (value: string | null) => {
            if (!value) return "—";
            return <OrderErrorDetailCell value={value} />;
          },
        };
      }
      if (col.key === "trackingNumber") {
        return {
          title,
          key: col.key,
          dataIndex: col.dataIndex,
          width: 92,
          render: (value: string | null) => {
            if (!value) return "—";
            return (
              <CopyableTruncatedCell
                value={value}
                copySuccessMessage="Tracking copiado"
                onlyWhenTruncated={false}
              />
            );
          },
        };
      }
      if (col.key === "orderVTEXId") {
        return {
          title,
          key: col.key,
          dataIndex: "orderVTEXId",
          // ASSUMPTION: VTEX order ids share fixed length (e.g. 1653501108115-01).
          width: 150,
          className: "orders-page__col--order-vtex",
          onHeaderCell: () => ({
            className: "orders-page__col--order-vtex",
          }),
          onCell: () => ({ className: "orders-page__col--order-vtex" }),
          render: (value: string) => (
            <CopyableTruncatedCell
              value={value}
              copySuccessMessage="Order VTEX copiado"
              onlyWhenTruncated={false}
            >
              <a
                className="orders-page__link copyable-truncated-cell__link"
                href={getVtexAdminOrderUrl(value)}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir orden en VTEX Admin"
                onClick={(event) => event.stopPropagation()}
              >
                <span className="orders-page__link-text copyable-truncated-cell__link-text">
                  {value}
                </span>
                <LinkOutlined className="orders-page__link-icon" aria-hidden />
              </a>
            </CopyableTruncatedCell>
          ),
        };
      }
      if (col.key === "orderSIESAId") {
        return {
          title,
          key: col.key,
          dataIndex: "orderSIESAId",
          width: 100,
          render: (value: string | null) => {
            if (!value) return "—";
            return (
              <CopyableTruncatedCell
                value={value}
                copySuccessMessage="Order SIESA copiado"
                onlyWhenTruncated={false}
              />
            );
          },
        };
      }
      if (col.key === "documentId") {
        return {
          title,
          key: col.key,
          dataIndex: "documentId",
          width: 96,
          className: "orders-page__col--document",
          onHeaderCell: () => ({
            className: "orders-page__col--document",
          }),
          onCell: () => ({ className: "orders-page__col--document" }),
          render: (value: string | null) => {
            if (!value) return "—";
            return (
              <CopyableTruncatedCell
                value={value}
                copySuccessMessage="Documento copiado"
                onlyWhenTruncated={false}
              />
            );
          },
        };
      }
      if (col.key === "names") {
        return {
          title,
          key: col.key,
          dataIndex: "names",
          width: 120,
          className: "orders-page__col--names",
          onHeaderCell: () => ({ className: "orders-page__col--names" }),
          onCell: () => ({ className: "orders-page__col--names" }),
          render: (value: string) => {
            if (!value) return "—";
            return (
              <CopyableTruncatedCell
                value={value}
                copySuccessMessage="Nombre copiado"
                onlyWhenTruncated={false}
              />
            );
          },
        };
      }
      if (col.key === "retryCount" || col.key === "attemptCount") {
        return {
          title,
          key: col.key,
          dataIndex: col.dataIndex,
          width: 84,
          align: "right" as const,
          render: (value: number | null) =>
            value == null ? "—" : String(value),
        };
      }

      const _exhaustive: never = col;
      throw new Error(`Unhandled order column: ${JSON.stringify(_exhaustive)}`);
    });
  }, [selectedColumns]);

  const exportColumns = useMemo(
    () =>
      COLUMN_DEFS.filter((col) => selectedColumns.includes(col.key)).map(
        (col) => ({
          title: col.tooltip,
          key: col.key,
        })
      ),
    [selectedColumns]
  );

  const mapOrderToExportRow = (order: OrderListItem) => ({
    documentId: order.documentId,
    orderVTEXId: order.orderVTEXId,
    orderSIESAId: order.orderSIESAId ?? "",
    names: order.names,
    trackingNumber: order.trackingNumber ?? "",
    status: getOrderStatusLabel(order.status),
    total: order.total,
    processedAt: order.processedAt
      ? TimeFormatter(order.processedAt)
      : "",
    retryCount: order.retryCount ?? "",
    attemptCount: order.attemptCount ?? "",
    errorDetail: order.errorDetail ?? "",
  });

  const exportData = useMemo(
    () => orderData.map(mapOrderToExportRow),
    [orderData]
  );

  const handleFetchAllExportData = async () => {
    const result = await fetchOrdersForExport(activeFilters);
    if (result.truncated) {
      // ASSUMPTION: duration 0 keeps the toast until the user closes it.
      message.open({
        type: "warning",
        key: EXPORT_TRUNCATED_MSG_KEY,
        duration: 0,
        content: (
          <span className="orders-page__export-limit-toast">
            <span>
              {`Se exportaron ${result.items.length.toLocaleString("es-CO")} de ${result.totalElements.toLocaleString("es-CO")} órdenes. Acota los filtros para descargar el resto.`}
            </span>
            <button
              type="button"
              className="orders-page__export-limit-toast-close"
              aria-label="Cerrar aviso"
              onClick={() => message.destroy(EXPORT_TRUNCATED_MSG_KEY)}
            >
              <CloseOutlined />
            </button>
          </span>
        ),
      });
    } else {
      message.destroy(EXPORT_TRUNCATED_MSG_KEY);
      message.success(
        `Se exportaron ${result.items.length.toLocaleString("es-CO")} ${result.items.length === 1 ? "orden" : "órdenes"}.`
      );
    }
    return result.items.map(mapOrderToExportRow);
  };

  const handleColumnSelection = (columnKey: OrderColumnKey, checked: boolean) => {
    setSelectedColumns((prev) =>
      checked ? [...prev, columnKey] : prev.filter((key) => key !== columnKey)
    );
  };

  const handleTableChange = (next: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: next.current,
      pageSize: next.pageSize,
    }));
  };

  const handleRefresh = () => {
    void fetchOrders(
      pagination.current || 1,
      pagination.pageSize || 10,
      activeFilters
    );
    if (hasNarrowingFilter) {
      void fetchFilteredCount(activeFilters);
    }
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setAppliedSearch("");
    setFilters(EMPTY_ORDER_FILTERS);
    setDateRange([null, null]);
    setPagination((prev) =>
      prev.current === 1 ? prev : { ...prev, current: 1 }
    );
  };

  const selectedEligibleIds = useMemo(
    () =>
      orderData
        .filter(
          (order) =>
            selectedRowKeys.includes(order.orderVTEXId) &&
            canReprocessOrder(order)
        )
        .map((order) => order.orderVTEXId),
    [orderData, selectedRowKeys]
  );

  const confirmReprocess = (orderIds: string[]) => {
    if (orderIds.length === 0) {
      message.warning(
        "Selecciona al menos una orden elegible para reprocesar."
      );
      return;
    }

    if (orderIds.length > ORDER_REPROCESS_MAX_BATCH) {
      message.warning(
        `Máximo ${ORDER_REPROCESS_MAX_BATCH} órdenes por lote. Reduce la selección.`
      );
      return;
    }

    Modal.confirm({
      title: "Reprocesar órdenes a SIESA",
      content: `Se enviará un lote de ${orderIds.length} orden(es) a reprocesar (máx. ${ORDER_REPROCESS_MAX_BATCH}). ¿Deseas continuar?`,
      okText: "Reprocesar",
      cancelText: "Cancelar",
      centered: true,
      // Do not return the reprocess Promise: confirm must close before progress opens.
      onOk: () => {
        void startReprocess(orderIds);
      },
    });
  };

  const columnMenu = (
    <Menu>
      {COLUMN_DEFS.map((column) => (
        <Menu.Item key={column.key}>
          <Checkbox
            checked={selectedColumns.includes(column.key)}
            onChange={(event) =>
              handleColumnSelection(column.key, event.target.checked)
            }
          >
            {column.tooltip}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  const rowSelection = {
    columnWidth: 28,
    selectedRowKeys,
    onChange: (keys: React.Key[], selectedRows: OrderListItem[]) => {
      const eligibleKeys = selectedRows
        .filter((row) => canReprocessOrder(row))
        .map((row) => row.orderVTEXId);

      if (eligibleKeys.length > ORDER_REPROCESS_MAX_BATCH) {
        message.warning(
          `Máximo ${ORDER_REPROCESS_MAX_BATCH} órdenes por lote.`
        );
        setSelectedRowKeys(eligibleKeys.slice(0, ORDER_REPROCESS_MAX_BATCH));
        return;
      }
      setSelectedRowKeys(eligibleKeys);
    },
    getCheckboxProps: (record: OrderListItem) => {
      const eligible = canReprocessOrder(record);
      return {
        disabled: isBusy || !eligible,
        title: eligible ? undefined : getReprocessIneligibilityReason(record),
        name: record.orderVTEXId,
      };
    },
  };

  return (
    <>
      <div className="page__container orders-page">
        {error && (
          <Alert
            className="orders-page__alert"
            type="error"
            showIcon
            message={error}
            action={
              <Button size="small" onClick={handleRefresh}>
                Reintentar
              </Button>
            }
          />
        )}

        <div className="orders-page__toolbar">
          <div className="orders-page__toolbar-group orders-page__toolbar-group--actions">
            <span className="orders-page__toolbar-label">Acciones</span>
            <div className="orders-page__toolbar-control-row">
              {selectedEligibleIds.length > 0 && (
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={reprocessLoading}
                  disabled={isBusy}
                  onClick={() => confirmReprocess(selectedEligibleIds)}
                >
                  Reprocesar a SIESA ({selectedEligibleIds.length})
                </Button>
              )}
              <Button
                type={selectedEligibleIds.length > 0 ? "default" : "primary"}
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
                disabled={reprocessLoading}
              >
                {loading ? "Actualizando" : "Actualizar"}
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={isBusy}
              >
                Limpiar filtros
              </Button>
              <ExportTableButton
                data={exportData}
                columns={exportColumns}
                fetchAllData={handleFetchAllExportData}
                fileNamePrefix="Ordenes"
                disabled={isBusy || totalElements === 0}
                variant="default"
              />
              <Dropdown
                overlay={columnMenu}
                trigger={["click"]}
                disabled={isBusy}
              >
                <Button
                  className="orders-page__toolbar-columns-btn"
                  disabled={isBusy}
                >
                  Columnas <DownOutlined />
                </Button>
              </Dropdown>
            </div>
            {selectedEligibleIds.length === 0 && (
              <p className="orders-page__toolbar-hint">
                Marca órdenes pendientes con el checkbox para reprocesar a
                SIESA.
              </p>
            )}
          </div>

          <div
            className="orders-page__toolbar-separator"
            aria-hidden="true"
          />

          <div className="orders-page__toolbar-group orders-page__toolbar-group--filters">
            <span className="orders-page__toolbar-label orders-page__toolbar-label--section">
              Filtros
            </span>
            <div className="orders-page__toolbar-filters-grid">
              <div className="orders-page__toolbar-field">
                <span className="orders-page__toolbar-label">Buscar por</span>
                <Select
                  className="orders-page__filter-type"
                  value={filters.filterType}
                  options={ORDER_FILTER_TYPE_OPTIONS}
                  disabled={isBusy}
                  onChange={(value: OrderFilterType) => {
                    setFilters((prev) => ({ ...prev, filterType: value }));
                    setSearchValue("");
                    setAppliedSearch("");
                    setPagination((prev) =>
                      prev.current === 1 ? prev : { ...prev, current: 1 }
                    );
                  }}
                />
              </div>

              <div className="orders-page__toolbar-field orders-page__toolbar-field--search">
                <span className="orders-page__toolbar-label">
                  Valor a buscar
                </span>
                <Input
                  className="orders-page__toolbar-search"
                  allowClear
                  disabled={isBusy}
                  placeholder={
                    ORDER_FILTER_TYPE_PLACEHOLDERS[filters.filterType]
                  }
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  onPressEnter={() => submitSearch()}
                  onClear={() => {
                    setSearchValue("");
                    submitSearch("");
                  }}
                  title="Coincidencia exacta en todo el listado"
                  suffix={
                    <button
                      type="button"
                      className="orders-page__toolbar-search-btn"
                      aria-label="Buscar"
                      disabled={isBusy}
                      onClick={() => submitSearch()}
                    >
                      <SearchOutlined />
                    </button>
                  }
                />
              </div>

              <div className="orders-page__toolbar-field">
                <span className="orders-page__toolbar-label">Estado</span>
                <Select
                  className="orders-page__filter-select"
                  value={filters.status}
                  options={ORDER_STATUS_FILTER_OPTIONS}
                  disabled={isBusy}
                  onChange={(value: string) => {
                    setFilters((prev) => ({ ...prev, status: value }));
                    setPagination((prev) =>
                      prev.current === 1 ? prev : { ...prev, current: 1 }
                    );
                  }}
                />
              </div>

              <div className="orders-page__toolbar-field orders-page__toolbar-field--dates">
                <span className="orders-page__toolbar-label">Fechas</span>
                <DatePicker.RangePicker
                  className="orders-page__date-range"
                  value={dateRange}
                  placeholder={["Desde", "Hasta"]}
                  disabled={isBusy}
                  disabledDate={isOrderListDateOutsideWindow}
                  onChange={(values) => {
                    setDateRange(
                      values
                        ? [values[0] ?? null, values[1] ?? null]
                        : [null, null]
                    );
                    setPagination((prev) =>
                      prev.current === 1 ? prev : { ...prev, current: 1 }
                    );
                  }}
                  format="YYYY-MM-DD"
                />
              </div>
            </div>
            <p className="orders-page__field-hint">
              Coincidencia exacta. Pulsa Enter o el icono de búsqueda para
              aplicar. Sin fechas, el listado ya viene limitado a los últimos{" "}
              {ORDER_LIST_WINDOW_MONTHS} meses.
            </p>
          </div>
        </div>

        {selectedEligibleIds.length > 0 && (
          <Alert
            className="orders-page__alert orders-page__alert--selection"
            type="info"
            showIcon
            message={
              <Space wrap>
                <Typography.Text>
                  {selectedEligibleIds.length}{" "}
                  {selectedEligibleIds.length === 1
                    ? "orden lista"
                    : "órdenes listas"}{" "}
                  para reprocesar.
                </Typography.Text>
                <Button
                  size="small"
                  type="link"
                  onClick={() => setSelectedRowKeys([])}
                >
                  Limpiar selección
                </Button>
              </Space>
            }
          />
        )}

        {showTableSkeleton ? (
          <TableSkeleton />
        ) : (
          <div className="data-enter">
            <Table<OrderListItem>
              className="orders__table__container"
              columns={columns}
              dataSource={orderData}
              loading={loading || reprocessLoading}
              rowKey="orderVTEXId"
              rowSelection={rowSelection}
              locale={{
                emptyText: error ? (
                  <Empty description="No se pudieron cargar las órdenes." />
                ) : appliedSearch ||
                  filters.status !== "ALL" ||
                  dateRange[0] ||
                  dateRange[1] ? (
                  <Empty
                    description={
                      <span>
                        Sin resultados para los filtros actuales.
                        <br />
                        <Button
                          type="link"
                          onClick={handleClearFilters}
                          disabled={isBusy}
                        >
                          Limpiar filtros
                        </Button>
                      </span>
                    }
                  />
                ) : (
                  <Empty description="No hay órdenes para mostrar." />
                ),
              }}
              pagination={{
                ...pagination,
                ...TABLE_PAGINATION_UX,
                total: paginationTotal,
                showTotal: (total) => {
                  if (
                    hasNarrowingFilter &&
                    filteredCountLoading &&
                    filteredCount === null
                  ) {
                    return "Contando órdenes…";
                  }
                  if (hasNarrowingFilter && filteredCountTruncated) {
                    return `${total.toLocaleString("es-CO")}+ órdenes (límite de conteo alcanzado)`;
                  }
                  return `${total.toLocaleString("es-CO")} ${total === 1 ? "orden" : "órdenes"}`;
                },
              }}
              onChange={handleTableChange}
              scroll={{ x: 1119 }}
            />
          </div>
        )}
      </div>

      <OrderReprocessProgressModal
        open={progressOpen}
        progress={progress}
        onClose={closeProgress}
      />
    </>
  );
};

export default OrdersPage;
